// ============================================================================
// Thought Definitions - Mood System
// ============================================================================
import { ConditionType, ThoughtCategory, ThoughtType } from './types.js';
// Helper to create thought definitions with defaults
const createThought = (partial) => ({
    type: partial.type,
    category: partial.category,
    moodOffset: partial.moodOffset,
    duration: partial.duration ?? 3600000, // 1 hour default
    stacking: partial.stacking ?? false,
    maxStacks: partial.maxStacks ?? 1,
    decayRate: partial.decayRate ?? 0,
    persistent: partial.persistent ?? false,
    triggeredBy: partial.triggeredBy ?? [],
});
// ============================================================================
// Thought Definitions
// ============================================================================
export const THOUGHT_DEFINITIONS = {
    // ===== ENVIRONMENT - POSITIVE =====
    [ThoughtType.BEAUTIFUL_ROOM]: createThought({
        type: ThoughtType.BEAUTIFUL_ROOM,
        category: ThoughtCategory.ENVIRONMENT_POSITIVE,
        moodOffset: 8,
        duration: 1800000, // 30 min
    }),
    [ThoughtType.SPACIOUS_ROOM]: createThought({
        type: ThoughtType.SPACIOUS_ROOM,
        category: ThoughtCategory.ENVIRONMENT_POSITIVE,
        moodOffset: 5,
        duration: 1800000,
    }),
    [ThoughtType.IMPRESSIVE_DINING]: createThought({
        type: ThoughtType.IMPRESSIVE_DINING,
        category: ThoughtCategory.ENVIRONMENT_POSITIVE,
        moodOffset: 6,
        duration: 1200000, // 20 min
    }),
    [ThoughtType.COMFORTABLE_BED]: createThought({
        type: ThoughtType.COMFORTABLE_BED,
        category: ThoughtCategory.ENVIRONMENT_POSITIVE,
        moodOffset: 7,
        duration: 7200000, // 2 hours
    }),
    [ThoughtType.OWN_ROOM]: createThought({
        type: ThoughtType.OWN_ROOM,
        category: ThoughtCategory.ENVIRONMENT_POSITIVE,
        moodOffset: 5,
        duration: 14400000, // 4 hours
    }),
    [ThoughtType.NATURE_BEAUTY]: createThought({
        type: ThoughtType.NATURE_BEAUTY,
        category: ThoughtCategory.ENVIRONMENT_POSITIVE,
        moodOffset: 6,
        duration: 900000, // 15 min
    }),
    // ===== ENVIRONMENT - NEGATIVE =====
    [ThoughtType.UGLY_ROOM]: createThought({
        type: ThoughtType.UGLY_ROOM,
        category: ThoughtCategory.ENVIRONMENT_NEGATIVE,
        moodOffset: -6,
        duration: 1800000,
    }),
    [ThoughtType.CRAMPED_ROOM]: createThought({
        type: ThoughtType.CRAMPED_ROOM,
        category: ThoughtCategory.ENVIRONMENT_NEGATIVE,
        moodOffset: -5,
        duration: 1800000,
    }),
    [ThoughtType.ATE_STANDING]: createThought({
        type: ThoughtType.ATE_STANDING,
        category: ThoughtCategory.ENVIRONMENT_NEGATIVE,
        moodOffset: -3,
        duration: 600000, // 10 min
    }),
    [ThoughtType.SLEPT_GROUND]: createThought({
        type: ThoughtType.SLEPT_GROUND,
        category: ThoughtCategory.ENVIRONMENT_NEGATIVE,
        moodOffset: -8,
        duration: 3600000,
    }),
    [ThoughtType.SLEPT_OUTSIDE]: createThought({
        type: ThoughtType.SLEPT_OUTSIDE,
        category: ThoughtCategory.ENVIRONMENT_NEGATIVE,
        moodOffset: -10,
        duration: 3600000,
    }),
    [ThoughtType.DISTURBED_SLEEP]: createThought({
        type: ThoughtType.DISTURBED_SLEEP,
        category: ThoughtCategory.ENVIRONMENT_NEGATIVE,
        moodOffset: -6,
        duration: 2400000, // 40 min
    }),
    [ThoughtType.DARKNESS]: createThought({
        type: ThoughtType.DARKNESS,
        category: ThoughtCategory.ENVIRONMENT_NEGATIVE,
        moodOffset: -4,
        duration: 600000,
    }),
    [ThoughtType.FILTHY_ENVIRONMENT]: createThought({
        type: ThoughtType.FILTHY_ENVIRONMENT,
        category: ThoughtCategory.ENVIRONMENT_NEGATIVE,
        moodOffset: -8,
        duration: 1200000,
    }),
    [ThoughtType.SAW_CORPSE]: createThought({
        type: ThoughtType.SAW_CORPSE,
        category: ThoughtCategory.ENVIRONMENT_NEGATIVE,
        moodOffset: -8,
        duration: 1800000,
        stacking: true,
        maxStacks: 5,
    }),
    [ThoughtType.SAW_ROTTING_CORPSE]: createThought({
        type: ThoughtType.SAW_ROTTING_CORPSE,
        category: ThoughtCategory.ENVIRONMENT_NEGATIVE,
        moodOffset: -15,
        duration: 2400000,
        stacking: true,
        maxStacks: 3,
    }),
    [ThoughtType.WITNESSED_BUTCHERING]: createThought({
        type: ThoughtType.WITNESSED_BUTCHERING,
        category: ThoughtCategory.ENVIRONMENT_NEGATIVE,
        moodOffset: -6,
        duration: 1200000,
    }),
    [ThoughtType.SMELLED_DECAY]: createThought({
        type: ThoughtType.SMELLED_DECAY,
        category: ThoughtCategory.ENVIRONMENT_NEGATIVE,
        moodOffset: -5,
        duration: 600000,
    }),
    [ThoughtType.HEARD_VIOLENCE]: createThought({
        type: ThoughtType.HEARD_VIOLENCE,
        category: ThoughtCategory.ENVIRONMENT_NEGATIVE,
        moodOffset: -6,
        duration: 900000,
        triggeredBy: [ConditionType.HYPERACUSIS, ConditionType.PTSD],
    }),
    [ThoughtType.UNCOMFORTABLE_TEMPERATURE]: createThought({
        type: ThoughtType.UNCOMFORTABLE_TEMPERATURE,
        category: ThoughtCategory.ENVIRONMENT_NEGATIVE,
        moodOffset: -5,
        duration: 300000, // 5 min
    }),
    // ===== NEEDS - SATISFIED =====
    [ThoughtType.ATE_FINE_MEAL]: createThought({
        type: ThoughtType.ATE_FINE_MEAL,
        category: ThoughtCategory.NEED_SATISFIED,
        moodOffset: 10,
        duration: 2400000, // 40 min
    }),
    [ThoughtType.ATE_LAVISH_MEAL]: createThought({
        type: ThoughtType.ATE_LAVISH_MEAL,
        category: ThoughtCategory.NEED_SATISFIED,
        moodOffset: 15,
        duration: 3600000,
    }),
    [ThoughtType.ATE_FAVORITE_FOOD]: createThought({
        type: ThoughtType.ATE_FAVORITE_FOOD,
        category: ThoughtCategory.NEED_SATISFIED,
        moodOffset: 12,
        duration: 2400000,
    }),
    [ThoughtType.WELL_RESTED]: createThought({
        type: ThoughtType.WELL_RESTED,
        category: ThoughtCategory.NEED_SATISFIED,
        moodOffset: 8,
        duration: 7200000, // 2 hours
    }),
    [ThoughtType.COMFORTABLE_TEMPERATURE_THOUGHT]: createThought({
        type: ThoughtType.COMFORTABLE_TEMPERATURE_THOUGHT,
        category: ThoughtCategory.NEED_SATISFIED,
        moodOffset: 3,
        duration: 600000,
    }),
    [ThoughtType.QUENCHED_THIRST]: createThought({
        type: ThoughtType.QUENCHED_THIRST,
        category: ThoughtCategory.NEED_SATISFIED,
        moodOffset: 4,
        duration: 1800000,
    }),
    // ===== NEEDS - UNSATISFIED =====
    [ThoughtType.ATE_RAW_FOOD]: createThought({
        type: ThoughtType.ATE_RAW_FOOD,
        category: ThoughtCategory.NEED_UNSATISFIED,
        moodOffset: -10,
        duration: 2400000,
    }),
    [ThoughtType.ATE_AWFUL_MEAL]: createThought({
        type: ThoughtType.ATE_AWFUL_MEAL,
        category: ThoughtCategory.NEED_UNSATISFIED,
        moodOffset: -8,
        duration: 1800000,
    }),
    [ThoughtType.ATE_CORPSE]: createThought({
        type: ThoughtType.ATE_CORPSE,
        category: ThoughtCategory.NEED_UNSATISFIED,
        moodOffset: -25,
        duration: 7200000, // 2 hours
        persistent: true,
    }),
    [ThoughtType.ATE_SAME_FOOD_REPEATEDLY]: createThought({
        type: ThoughtType.ATE_SAME_FOOD_REPEATEDLY,
        category: ThoughtCategory.NEED_UNSATISFIED,
        moodOffset: -5,
        duration: 3600000,
        stacking: true,
        maxStacks: 4,
    }),
    [ThoughtType.HUNGRY]: createThought({
        type: ThoughtType.HUNGRY,
        category: ThoughtCategory.NEED_UNSATISFIED,
        moodOffset: -6,
        duration: 600000,
    }),
    [ThoughtType.VERY_HUNGRY]: createThought({
        type: ThoughtType.VERY_HUNGRY,
        category: ThoughtCategory.NEED_UNSATISFIED,
        moodOffset: -12,
        duration: 600000,
    }),
    [ThoughtType.STARVING_THOUGHT]: createThought({
        type: ThoughtType.STARVING_THOUGHT,
        category: ThoughtCategory.NEED_UNSATISFIED,
        moodOffset: -25,
        duration: 600000,
        triggeredBy: [ConditionType.STARVING_SEVERE],
    }),
    [ThoughtType.TIRED]: createThought({
        type: ThoughtType.TIRED,
        category: ThoughtCategory.NEED_UNSATISFIED,
        moodOffset: -5,
        duration: 600000,
    }),
    [ThoughtType.EXHAUSTED_THOUGHT]: createThought({
        type: ThoughtType.EXHAUSTED_THOUGHT,
        category: ThoughtCategory.NEED_UNSATISFIED,
        moodOffset: -15,
        duration: 600000,
        triggeredBy: [ConditionType.EXHAUSTED],
    }),
    [ThoughtType.SLEEP_DEPRIVED_THOUGHT]: createThought({
        type: ThoughtType.SLEEP_DEPRIVED_THOUGHT,
        category: ThoughtCategory.NEED_UNSATISFIED,
        moodOffset: -20,
        duration: 1200000,
        triggeredBy: [ConditionType.SLEEP_DEPRIVED],
    }),
    [ThoughtType.THIRSTY]: createThought({
        type: ThoughtType.THIRSTY,
        category: ThoughtCategory.NEED_UNSATISFIED,
        moodOffset: -5,
        duration: 600000,
    }),
    [ThoughtType.VERY_THIRSTY]: createThought({
        type: ThoughtType.VERY_THIRSTY,
        category: ThoughtCategory.NEED_UNSATISFIED,
        moodOffset: -15,
        duration: 600000,
    }),
    [ThoughtType.IN_PAIN]: createThought({
        type: ThoughtType.IN_PAIN,
        category: ThoughtCategory.NEED_UNSATISFIED,
        moodOffset: -12,
        duration: 300000, // 5 min - refreshes while in pain
    }),
    [ThoughtType.IN_EXTREME_PAIN]: createThought({
        type: ThoughtType.IN_EXTREME_PAIN,
        category: ThoughtCategory.NEED_UNSATISFIED,
        moodOffset: -30,
        duration: 300000,
    }),
    // ===== SOCIAL - POSITIVE =====
    [ThoughtType.HAD_NICE_CHAT]: createThought({
        type: ThoughtType.HAD_NICE_CHAT,
        category: ThoughtCategory.SOCIAL_POSITIVE,
        moodOffset: 6,
        duration: 2400000,
        stacking: true,
        maxStacks: 3,
    }),
    [ThoughtType.MADE_FRIEND]: createThought({
        type: ThoughtType.MADE_FRIEND,
        category: ThoughtCategory.SOCIAL_POSITIVE,
        moodOffset: 15,
        duration: 14400000, // 4 hours
        persistent: true,
    }),
    [ThoughtType.ROMANCED]: createThought({
        type: ThoughtType.ROMANCED,
        category: ThoughtCategory.SOCIAL_POSITIVE,
        moodOffset: 20,
        duration: 7200000,
    }),
    [ThoughtType.GOT_MARRIED]: createThought({
        type: ThoughtType.GOT_MARRIED,
        category: ThoughtCategory.SOCIAL_POSITIVE,
        moodOffset: 40,
        duration: 86400000, // 24 hours
        persistent: true,
    }),
    [ThoughtType.HAD_PARTY]: createThought({
        type: ThoughtType.HAD_PARTY,
        category: ThoughtCategory.SOCIAL_POSITIVE,
        moodOffset: 12,
        duration: 7200000,
    }),
    [ThoughtType.RECEIVED_GIFT]: createThought({
        type: ThoughtType.RECEIVED_GIFT,
        category: ThoughtCategory.SOCIAL_POSITIVE,
        moodOffset: 10,
        duration: 3600000,
    }),
    [ThoughtType.HELPED_SOMEONE]: createThought({
        type: ThoughtType.HELPED_SOMEONE,
        category: ThoughtCategory.SOCIAL_POSITIVE,
        moodOffset: 8,
        duration: 2400000,
        stacking: true,
        maxStacks: 3,
    }),
    [ThoughtType.WAS_HELPED]: createThought({
        type: ThoughtType.WAS_HELPED,
        category: ThoughtCategory.SOCIAL_POSITIVE,
        moodOffset: 7,
        duration: 2400000,
    }),
    [ThoughtType.COMPLIMENTED]: createThought({
        type: ThoughtType.COMPLIMENTED,
        category: ThoughtCategory.SOCIAL_POSITIVE,
        moodOffset: 5,
        duration: 1800000,
        stacking: true,
        maxStacks: 3,
    }),
    // ===== SOCIAL - NEGATIVE =====
    [ThoughtType.INSULTED]: createThought({
        type: ThoughtType.INSULTED,
        category: ThoughtCategory.SOCIAL_NEGATIVE,
        moodOffset: -8,
        duration: 2400000,
        stacking: true,
        maxStacks: 5,
    }),
    [ThoughtType.REJECTED]: createThought({
        type: ThoughtType.REJECTED,
        category: ThoughtCategory.SOCIAL_NEGATIVE,
        moodOffset: -15,
        duration: 7200000,
    }),
    [ThoughtType.IGNORED]: createThought({
        type: ThoughtType.IGNORED,
        category: ThoughtCategory.SOCIAL_NEGATIVE,
        moodOffset: -5,
        duration: 1800000,
        stacking: true,
        maxStacks: 4,
    }),
    [ThoughtType.BETRAYED]: createThought({
        type: ThoughtType.BETRAYED,
        category: ThoughtCategory.SOCIAL_NEGATIVE,
        moodOffset: -30,
        duration: 14400000, // 4 hours
        persistent: true,
    }),
    [ThoughtType.CHEATED_ON]: createThought({
        type: ThoughtType.CHEATED_ON,
        category: ThoughtCategory.SOCIAL_NEGATIVE,
        moodOffset: -35,
        duration: 28800000, // 8 hours
        persistent: true,
    }),
    [ThoughtType.DIVORCED]: createThought({
        type: ThoughtType.DIVORCED,
        category: ThoughtCategory.SOCIAL_NEGATIVE,
        moodOffset: -25,
        duration: 43200000, // 12 hours
        persistent: true,
    }),
    [ThoughtType.ARGUED]: createThought({
        type: ThoughtType.ARGUED,
        category: ThoughtCategory.SOCIAL_NEGATIVE,
        moodOffset: -8,
        duration: 2400000,
        stacking: true,
        maxStacks: 3,
    }),
    [ThoughtType.WITNESSED_ARGUMENT]: createThought({
        type: ThoughtType.WITNESSED_ARGUMENT,
        category: ThoughtCategory.SOCIAL_NEGATIVE,
        moodOffset: -4,
        duration: 1200000,
    }),
    [ThoughtType.SOCIAL_ISOLATION]: createThought({
        type: ThoughtType.SOCIAL_ISOLATION,
        category: ThoughtCategory.SOCIAL_NEGATIVE,
        moodOffset: -12,
        duration: 7200000,
        decayRate: 0.1,
    }),
    [ThoughtType.OVERCROWDED]: createThought({
        type: ThoughtType.OVERCROWDED,
        category: ThoughtCategory.SOCIAL_NEGATIVE,
        moodOffset: -6,
        duration: 1800000,
    }),
    // ===== WORK =====
    [ThoughtType.COMPLETED_PROJECT]: createThought({
        type: ThoughtType.COMPLETED_PROJECT,
        category: ThoughtCategory.WORK_POSITIVE,
        moodOffset: 12,
        duration: 3600000,
    }),
    [ThoughtType.SKILL_IMPROVED]: createThought({
        type: ThoughtType.SKILL_IMPROVED,
        category: ThoughtCategory.WORK_POSITIVE,
        moodOffset: 6,
        duration: 1800000,
        stacking: true,
        maxStacks: 3,
    }),
    [ThoughtType.CREATIVE_WORK]: createThought({
        type: ThoughtType.CREATIVE_WORK,
        category: ThoughtCategory.WORK_POSITIVE,
        moodOffset: 8,
        duration: 2400000,
    }),
    [ThoughtType.HELPED_COLONY]: createThought({
        type: ThoughtType.HELPED_COLONY,
        category: ThoughtCategory.WORK_POSITIVE,
        moodOffset: 7,
        duration: 2400000,
    }),
    [ThoughtType.RESCUED_SOMEONE]: createThought({
        type: ThoughtType.RESCUED_SOMEONE,
        category: ThoughtCategory.WORK_POSITIVE,
        moodOffset: 15,
        duration: 7200000,
    }),
    [ThoughtType.BORING_WORK]: createThought({
        type: ThoughtType.BORING_WORK,
        category: ThoughtCategory.WORK_NEGATIVE,
        moodOffset: -4,
        duration: 1800000,
        stacking: true,
        maxStacks: 5,
    }),
    [ThoughtType.FAILED_TASK]: createThought({
        type: ThoughtType.FAILED_TASK,
        category: ThoughtCategory.WORK_NEGATIVE,
        moodOffset: -8,
        duration: 2400000,
        stacking: true,
        maxStacks: 3,
    }),
    [ThoughtType.OVERWORKED]: createThought({
        type: ThoughtType.OVERWORKED,
        category: ThoughtCategory.WORK_NEGATIVE,
        moodOffset: -10,
        duration: 3600000,
    }),
    [ThoughtType.UNDERWORKED]: createThought({
        type: ThoughtType.UNDERWORKED,
        category: ThoughtCategory.WORK_NEGATIVE,
        moodOffset: -5,
        duration: 3600000,
    }),
    [ThoughtType.FORCED_LABOR]: createThought({
        type: ThoughtType.FORCED_LABOR,
        category: ThoughtCategory.WORK_NEGATIVE,
        moodOffset: -20,
        duration: 7200000,
        persistent: true,
    }),
    // ===== HEALTH =====
    [ThoughtType.HEALED]: createThought({
        type: ThoughtType.HEALED,
        category: ThoughtCategory.HEALTH_POSITIVE,
        moodOffset: 8,
        duration: 3600000,
    }),
    [ThoughtType.SURGERY_SUCCESS]: createThought({
        type: ThoughtType.SURGERY_SUCCESS,
        category: ThoughtCategory.HEALTH_POSITIVE,
        moodOffset: 10,
        duration: 7200000,
    }),
    [ThoughtType.GOT_PROSTHETIC]: createThought({
        type: ThoughtType.GOT_PROSTHETIC,
        category: ThoughtCategory.HEALTH_POSITIVE,
        moodOffset: 12,
        duration: 14400000, // 4 hours
    }),
    [ThoughtType.RECOVERED_FROM_ILLNESS]: createThought({
        type: ThoughtType.RECOVERED_FROM_ILLNESS,
        category: ThoughtCategory.HEALTH_POSITIVE,
        moodOffset: 10,
        duration: 7200000,
    }),
    [ThoughtType.INJURED_THOUGHT]: createThought({
        type: ThoughtType.INJURED_THOUGHT,
        category: ThoughtCategory.HEALTH_NEGATIVE,
        moodOffset: -10,
        duration: 1800000,
    }),
    [ThoughtType.SICK_THOUGHT]: createThought({
        type: ThoughtType.SICK_THOUGHT,
        category: ThoughtCategory.HEALTH_NEGATIVE,
        moodOffset: -8,
        duration: 1800000,
    }),
    [ThoughtType.CHRONIC_PAIN_THOUGHT]: createThought({
        type: ThoughtType.CHRONIC_PAIN_THOUGHT,
        category: ThoughtCategory.HEALTH_NEGATIVE,
        moodOffset: -15,
        duration: 3600000,
        triggeredBy: [ConditionType.CHRONIC_PAIN, ConditionType.FIBROMYALGIA],
    }),
    [ThoughtType.SCARRED]: createThought({
        type: ThoughtType.SCARRED,
        category: ThoughtCategory.HEALTH_NEGATIVE,
        moodOffset: -5,
        duration: 7200000,
        persistent: true,
        triggeredBy: [ConditionType.SCAR_DISFIGURING],
    }),
    [ThoughtType.DISFIGURED]: createThought({
        type: ThoughtType.DISFIGURED,
        category: ThoughtCategory.HEALTH_NEGATIVE,
        moodOffset: -20,
        duration: 14400000,
        persistent: true,
    }),
    [ThoughtType.LOST_LIMB]: createThought({
        type: ThoughtType.LOST_LIMB,
        category: ThoughtCategory.HEALTH_NEGATIVE,
        moodOffset: -30,
        duration: 86400000, // 24 hours
        persistent: true,
        decayRate: 0.05,
        triggeredBy: [ConditionType.MISSING_PART, ConditionType.AMPUTATED_CLEAN],
    }),
    [ThoughtType.ORGAN_HARVESTED]: createThought({
        type: ThoughtType.ORGAN_HARVESTED,
        category: ThoughtCategory.HEALTH_NEGATIVE,
        moodOffset: -35,
        duration: 172800000, // 48 hours
        persistent: true,
    }),
    // ===== TRAUMA =====
    [ThoughtType.WITNESSED_DEATH]: createThought({
        type: ThoughtType.WITNESSED_DEATH,
        category: ThoughtCategory.TRAUMA,
        moodOffset: -20,
        duration: 7200000,
        stacking: true,
        maxStacks: 3,
        persistent: true,
    }),
    [ThoughtType.WITNESSED_MURDER]: createThought({
        type: ThoughtType.WITNESSED_MURDER,
        category: ThoughtCategory.TRAUMA,
        moodOffset: -30,
        duration: 14400000,
        persistent: true,
    }),
    [ThoughtType.KILLED_SOMEONE]: createThought({
        type: ThoughtType.KILLED_SOMEONE,
        category: ThoughtCategory.TRAUMA,
        moodOffset: -15,
        duration: 7200000,
        stacking: true,
        maxStacks: 5,
        persistent: true,
    }),
    [ThoughtType.KILLED_INNOCENT]: createThought({
        type: ThoughtType.KILLED_INNOCENT,
        category: ThoughtCategory.TRAUMA,
        moodOffset: -40,
        duration: 172800000, // 48 hours
        persistent: true,
    }),
    [ThoughtType.TORTURED_SOMEONE]: createThought({
        type: ThoughtType.TORTURED_SOMEONE,
        category: ThoughtCategory.TRAUMA,
        moodOffset: -25,
        duration: 28800000, // 8 hours
        persistent: true,
    }),
    [ThoughtType.WAS_TORTURED]: createThought({
        type: ThoughtType.WAS_TORTURED,
        category: ThoughtCategory.TRAUMA,
        moodOffset: -50,
        duration: 259200000, // 72 hours
        persistent: true,
    }),
    [ThoughtType.NEAR_DEATH_EXPERIENCE]: createThought({
        type: ThoughtType.NEAR_DEATH_EXPERIENCE,
        category: ThoughtCategory.TRAUMA,
        moodOffset: -20,
        duration: 14400000,
        persistent: true,
    }),
    [ThoughtType.SURVIVED_ATTACK]: createThought({
        type: ThoughtType.SURVIVED_ATTACK,
        category: ThoughtCategory.TRAUMA,
        moodOffset: -15,
        duration: 7200000,
        persistent: true,
    }),
    [ThoughtType.LOST_LOVED_ONE]: createThought({
        type: ThoughtType.LOST_LOVED_ONE,
        category: ThoughtCategory.TRAUMA,
        moodOffset: -45,
        duration: 604800000, // 7 days
        persistent: true,
        decayRate: 0.02,
    }),
    [ThoughtType.LOST_PET]: createThought({
        type: ThoughtType.LOST_PET,
        category: ThoughtCategory.TRAUMA,
        moodOffset: -20,
        duration: 86400000, // 24 hours
        persistent: true,
        decayRate: 0.05,
    }),
    [ThoughtType.HOME_DESTROYED]: createThought({
        type: ThoughtType.HOME_DESTROYED,
        category: ThoughtCategory.TRAUMA,
        moodOffset: -30,
        duration: 172800000, // 48 hours
        persistent: true,
    }),
    [ThoughtType.CAPTURED]: createThought({
        type: ThoughtType.CAPTURED,
        category: ThoughtCategory.TRAUMA,
        moodOffset: -35,
        duration: 14400000,
        persistent: true,
    }),
    [ThoughtType.ENSLAVED]: createThought({
        type: ThoughtType.ENSLAVED,
        category: ThoughtCategory.TRAUMA,
        moodOffset: -50,
        duration: 86400000,
        persistent: true,
    }),
    [ThoughtType.ESCAPED_CAPTIVITY]: createThought({
        type: ThoughtType.ESCAPED_CAPTIVITY,
        category: ThoughtCategory.TRAUMA,
        moodOffset: 15, // Relief
        duration: 14400000,
    }),
    [ThoughtType.PTSD_FLASHBACK]: createThought({
        type: ThoughtType.PTSD_FLASHBACK,
        category: ThoughtCategory.TRAUMA,
        moodOffset: -25,
        duration: 1800000,
        triggeredBy: [ConditionType.PTSD, ConditionType.COMPLEX_PTSD],
    }),
    [ThoughtType.NIGHTMARE_THOUGHT]: createThought({
        type: ThoughtType.NIGHTMARE_THOUGHT,
        category: ThoughtCategory.TRAUMA,
        moodOffset: -10,
        duration: 3600000,
    }),
    // ===== RELATIONSHIPS =====
    [ThoughtType.LOVED_ONE_NEARBY]: createThought({
        type: ThoughtType.LOVED_ONE_NEARBY,
        category: ThoughtCategory.RELATIONSHIP_POSITIVE,
        moodOffset: 10,
        duration: 3600000,
    }),
    [ThoughtType.FAMILY_REUNITED]: createThought({
        type: ThoughtType.FAMILY_REUNITED,
        category: ThoughtCategory.RELATIONSHIP_POSITIVE,
        moodOffset: 25,
        duration: 28800000, // 8 hours
        persistent: true,
    }),
    [ThoughtType.CHILD_BORN]: createThought({
        type: ThoughtType.CHILD_BORN,
        category: ThoughtCategory.RELATIONSHIP_POSITIVE,
        moodOffset: 30,
        duration: 172800000, // 48 hours
        persistent: true,
    }),
    [ThoughtType.PET_BONDING]: createThought({
        type: ThoughtType.PET_BONDING,
        category: ThoughtCategory.RELATIONSHIP_POSITIVE,
        moodOffset: 8,
        duration: 3600000,
        stacking: true,
        maxStacks: 3,
    }),
    [ThoughtType.LOVED_ONE_FAR]: createThought({
        type: ThoughtType.LOVED_ONE_FAR,
        category: ThoughtCategory.RELATIONSHIP_NEGATIVE,
        moodOffset: -8,
        duration: 7200000,
    }),
    [ThoughtType.FAMILY_SEPARATED]: createThought({
        type: ThoughtType.FAMILY_SEPARATED,
        category: ThoughtCategory.RELATIONSHIP_NEGATIVE,
        moodOffset: -15,
        duration: 14400000,
        persistent: true,
    }),
    [ThoughtType.RELATIONSHIP_ENDED]: createThought({
        type: ThoughtType.RELATIONSHIP_ENDED,
        category: ThoughtCategory.RELATIONSHIP_NEGATIVE,
        moodOffset: -20,
        duration: 43200000, // 12 hours
        persistent: true,
        decayRate: 0.05,
    }),
    [ThoughtType.RIVAL_NEARBY]: createThought({
        type: ThoughtType.RIVAL_NEARBY,
        category: ThoughtCategory.RELATIONSHIP_NEGATIVE,
        moodOffset: -5,
        duration: 1800000,
    }),
    // ===== PERSONALITY/TRAITS =====
    [ThoughtType.ENJOYED_SOLITUDE]: createThought({
        type: ThoughtType.ENJOYED_SOLITUDE,
        category: ThoughtCategory.PERSONALITY_TRAIT,
        moodOffset: 6,
        duration: 3600000,
    }),
    [ThoughtType.ENJOYED_SOCIALIZING]: createThought({
        type: ThoughtType.ENJOYED_SOCIALIZING,
        category: ThoughtCategory.PERSONALITY_TRAIT,
        moodOffset: 8,
        duration: 3600000,
    }),
    [ThoughtType.OUTDOOR_LOVER]: createThought({
        type: ThoughtType.OUTDOOR_LOVER,
        category: ThoughtCategory.PERSONALITY_TRAIT,
        moodOffset: 5,
        duration: 2400000,
    }),
    [ThoughtType.INDOOR_COMFORT]: createThought({
        type: ThoughtType.INDOOR_COMFORT,
        category: ThoughtCategory.PERSONALITY_TRAIT,
        moodOffset: 4,
        duration: 2400000,
    }),
    [ThoughtType.CREATIVE_EXPRESSION]: createThought({
        type: ThoughtType.CREATIVE_EXPRESSION,
        category: ThoughtCategory.PERSONALITY_TRAIT,
        moodOffset: 10,
        duration: 3600000,
    }),
    [ThoughtType.INTELLECTUAL_PURSUIT]: createThought({
        type: ThoughtType.INTELLECTUAL_PURSUIT,
        category: ThoughtCategory.PERSONALITY_TRAIT,
        moodOffset: 8,
        duration: 3600000,
    }),
    [ThoughtType.PHYSICAL_ACTIVITY]: createThought({
        type: ThoughtType.PHYSICAL_ACTIVITY,
        category: ThoughtCategory.PERSONALITY_TRAIT,
        moodOffset: 6,
        duration: 2400000,
    }),
    [ThoughtType.NEEDS_SOLITUDE]: createThought({
        type: ThoughtType.NEEDS_SOLITUDE,
        category: ThoughtCategory.PERSONALITY_TRAIT,
        moodOffset: -6,
        duration: 3600000,
    }),
    [ThoughtType.NEEDS_PEOPLE]: createThought({
        type: ThoughtType.NEEDS_PEOPLE,
        category: ThoughtCategory.PERSONALITY_TRAIT,
        moodOffset: -8,
        duration: 3600000,
    }),
    [ThoughtType.CABIN_FEVER]: createThought({
        type: ThoughtType.CABIN_FEVER,
        category: ThoughtCategory.PERSONALITY_TRAIT,
        moodOffset: -10,
        duration: 7200000,
    }),
    [ThoughtType.MISSES_OUTDOORS]: createThought({
        type: ThoughtType.MISSES_OUTDOORS,
        category: ThoughtCategory.PERSONALITY_TRAIT,
        moodOffset: -6,
        duration: 3600000,
    }),
    [ThoughtType.BORED]: createThought({
        type: ThoughtType.BORED,
        category: ThoughtCategory.PERSONALITY_TRAIT,
        moodOffset: -5,
        duration: 2400000,
    }),
    [ThoughtType.UNDERSTIMULATED]: createThought({
        type: ThoughtType.UNDERSTIMULATED,
        category: ThoughtCategory.PERSONALITY_TRAIT,
        moodOffset: -8,
        duration: 3600000,
    }),
    [ThoughtType.OVERSTIMULATED]: createThought({
        type: ThoughtType.OVERSTIMULATED,
        category: ThoughtCategory.PERSONALITY_TRAIT,
        moodOffset: -10,
        duration: 2400000,
        triggeredBy: [ConditionType.AUTISM_SPECTRUM, ConditionType.HYPERACUSIS],
    }),
    // ===== EVENTS =====
    [ThoughtType.CELEBRATION]: createThought({
        type: ThoughtType.CELEBRATION,
        category: ThoughtCategory.EVENT_POSITIVE,
        moodOffset: 15,
        duration: 7200000,
        stacking: true,
        maxStacks: 2,
    }),
    [ThoughtType.VICTORY]: createThought({
        type: ThoughtType.VICTORY,
        category: ThoughtCategory.EVENT_POSITIVE,
        moodOffset: 20,
        duration: 14400000,
    }),
    [ThoughtType.GOOD_NEWS]: createThought({
        type: ThoughtType.GOOD_NEWS,
        category: ThoughtCategory.EVENT_POSITIVE,
        moodOffset: 10,
        duration: 3600000,
    }),
    [ThoughtType.DISCOVERED_SOMETHING]: createThought({
        type: ThoughtType.DISCOVERED_SOMETHING,
        category: ThoughtCategory.EVENT_POSITIVE,
        moodOffset: 12,
        duration: 3600000,
    }),
    [ThoughtType.DISASTER]: createThought({
        type: ThoughtType.DISASTER,
        category: ThoughtCategory.EVENT_NEGATIVE,
        moodOffset: -25,
        duration: 14400000,
        persistent: true,
    }),
    [ThoughtType.DEFEAT]: createThought({
        type: ThoughtType.DEFEAT,
        category: ThoughtCategory.EVENT_NEGATIVE,
        moodOffset: -15,
        duration: 7200000,
    }),
    [ThoughtType.BAD_NEWS]: createThought({
        type: ThoughtType.BAD_NEWS,
        category: ThoughtCategory.EVENT_NEGATIVE,
        moodOffset: -10,
        duration: 3600000,
    }),
    [ThoughtType.BETRAYAL]: createThought({
        type: ThoughtType.BETRAYAL,
        category: ThoughtCategory.EVENT_NEGATIVE,
        moodOffset: -35,
        duration: 28800000,
        persistent: true,
    }),
    // ===== EXISTENTIAL =====
    [ThoughtType.SENSE_OF_PURPOSE]: createThought({
        type: ThoughtType.SENSE_OF_PURPOSE,
        category: ThoughtCategory.EXISTENTIAL,
        moodOffset: 15,
        duration: 14400000,
    }),
    [ThoughtType.HOPE]: createThought({
        type: ThoughtType.HOPE,
        category: ThoughtCategory.EXISTENTIAL,
        moodOffset: 10,
        duration: 7200000,
    }),
    [ThoughtType.ACCOMPLISHMENT]: createThought({
        type: ThoughtType.ACCOMPLISHMENT,
        category: ThoughtCategory.EXISTENTIAL,
        moodOffset: 12,
        duration: 7200000,
        stacking: true,
        maxStacks: 3,
    }),
    [ThoughtType.SPIRITUAL_PEACE]: createThought({
        type: ThoughtType.SPIRITUAL_PEACE,
        category: ThoughtCategory.EXISTENTIAL,
        moodOffset: 10,
        duration: 7200000,
    }),
    [ThoughtType.EXISTENTIAL_DREAD]: createThought({
        type: ThoughtType.EXISTENTIAL_DREAD,
        category: ThoughtCategory.EXISTENTIAL,
        moodOffset: -20,
        duration: 14400000,
        triggeredBy: [ConditionType.MAJOR_DEPRESSION, ConditionType.PTSD],
    }),
    [ThoughtType.HOPELESSNESS]: createThought({
        type: ThoughtType.HOPELESSNESS,
        category: ThoughtCategory.EXISTENTIAL,
        moodOffset: -25,
        duration: 14400000,
        triggeredBy: [ConditionType.MAJOR_DEPRESSION],
    }),
    [ThoughtType.MEANINGLESSNESS]: createThought({
        type: ThoughtType.MEANINGLESSNESS,
        category: ThoughtCategory.EXISTENTIAL,
        moodOffset: -15,
        duration: 7200000,
        triggeredBy: [ConditionType.MAJOR_DEPRESSION, ConditionType.PERSISTENT_DEPRESSION],
    }),
    [ThoughtType.GUILT]: createThought({
        type: ThoughtType.GUILT,
        category: ThoughtCategory.EXISTENTIAL,
        moodOffset: -15,
        duration: 7200000,
        stacking: true,
        maxStacks: 5,
        persistent: true,
    }),
    [ThoughtType.SHAME]: createThought({
        type: ThoughtType.SHAME,
        category: ThoughtCategory.EXISTENTIAL,
        moodOffset: -12,
        duration: 7200000,
        persistent: true,
    }),
    [ThoughtType.REGRET]: createThought({
        type: ThoughtType.REGRET,
        category: ThoughtCategory.EXISTENTIAL,
        moodOffset: -10,
        duration: 14400000,
        stacking: true,
        maxStacks: 5,
        persistent: true,
        decayRate: 0.02,
    }),
};
// ============================================================================
// Helper Functions
// ============================================================================
export const getThoughtDefinition = ({ type, }) => {
    return THOUGHT_DEFINITIONS[type];
};
export const getThoughtsByCategory = ({ category, }) => {
    const results = [];
    for (const [typeStr, def] of Object.entries(THOUGHT_DEFINITIONS)) {
        if (def && def.category === category) {
            results.push(Number(typeStr));
        }
    }
    return results;
};
export const getPositiveThoughts = () => {
    const results = [];
    for (const [typeStr, def] of Object.entries(THOUGHT_DEFINITIONS)) {
        if (def && def.moodOffset > 0) {
            results.push(Number(typeStr));
        }
    }
    return results;
};
export const getNegativeThoughts = () => {
    const results = [];
    for (const [typeStr, def] of Object.entries(THOUGHT_DEFINITIONS)) {
        if (def && def.moodOffset < 0) {
            results.push(Number(typeStr));
        }
    }
    return results;
};
//# sourceMappingURL=thoughts.js.map